import ConditionalToolTip from "components/design/conditionalToolTip";
import ToolTip from "components/design/toolTip";
import { Button } from "components/DesignSystem/Button/Button";
import { DateField } from "components/DesignSystem/DateInput/DateField";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import Modal from "components/DesignSystem/Modal/Modal";
import { CalendarIcon } from "components/icons/CalendarIcon";
import { Info } from "components/icons/Info";
import { OptionDropdown } from "components/icons/optionDropdown";
import { SendInvoiceModal } from "components/SendInvoiceModal/SendInvoiceModal";
import {
  DUPLICATED_INVOICE_FROM_THREE_DOTS,
  MARKED_INVOICE_AS_PAID_FROM_THREE_DOTS,
  MARKED_INVOICE_AS_SENT_FROM_THREE_DOTS,
} from "constants/analyticsEvents";
import { INVOICE_STATUSES } from "constants/invoicing";
import {
  MANUAL_INVOICE_SOURCES,
  RESYNCABLE_INVOICE_SOURCES,
  REVENUE_DATA_SOURCE,
} from "constants/revenueDataSource";
import { useAnalytics } from "hooks/useAnalytics";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import { useToast } from "hooks/useToast";
import { useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import {
  invoiceApis,
  useCreateEntityInvoiceMutation,
  useDeleteEntityInvoiceMutation,
  useGenrateShortLinkMutation,
  useLazyDownloadInvoiceQuery,
  useLazyDownloadPackingSlipQuery,
  useLazyGetEntityInvoiceQuery,
  useUpdateInvoiceMutation,
} from "store/apis/invoices";
import { useLazyGetPreviewUrlQuery } from "store/apis/previewUrl";
import { Invoice } from "types/Models/invoice";
import { BackendError } from "types/utils/error";
import { openLink } from "utils/openLink";
import { LinkInvoiceToTransactionModal } from "./LinkInvoiceToTransactionModal";
import { WriteOffPendingAmountModal } from "./WriteOffPendingAmountModal";
import { ResyncInvoiceModal } from "./ResyncInvoiceModal";
import { FormikForm } from "components/FormikForm/FormikForm";
import { SmallInfo } from "components/icons/SmallInfo";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import dayjs from "dayjs";
import { DeleteInvoiceWarningModal } from "components/InvoiceList/DeleteInvoiceWarningModal";
import { useGetBookkeepingSettingsQuery } from "store/apis/booksLockDate";
import { LockDateToolTipContent } from "components/LockedDatePicker/LockedDatePicker";
import { LockedUi } from "components/LockedUi/LockedUi";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";

export const InvoiceActions = ({
  invoice,
  isFromInvoiceDetails,
}: {
  invoice: Invoice;
  isFromInvoiceDetails?: boolean;
}) => {
  const query = useQuery();
  const isSendInvoiceOpen = Boolean(query.get("is_send_invoice_open"));
  const group = useCurrentGroupContext();
  const history = useHistory();
  const [duplicating, setDuplicating] = useState(false);
  const { search } = useLocation();
  const sendInvoice = useModal(isSendInvoiceOpen);
  const entityId = useCurrentEntityId();
  const isManualInvoice = MANUAL_INVOICE_SOURCES.includes(invoice.source);
  const isStripeInvoice = !MANUAL_INVOICE_SOURCES.includes(invoice.source);
  const isHubspotInvoice = invoice.source !== REVENUE_DATA_SOURCE.HUBSPOT;
  const isBillComInvoice = invoice.source !== REVENUE_DATA_SOURCE.BILL_COM;
  const docId = invoice.document?.file_id;

  const linkViaModal = useModal();

  const { customerId: customerIdFromUrl } = useParams<{
    customerId?: string;
  }>();

  const [
    deleteInvoice,
    { isLoading: deletingInvoice, originalArgs: deletingArgs },
  ] = useDeleteEntityInvoiceMutation();

  const [
    updateInvoice,
    { isLoading: updatingInvoice, originalArgs: updateInvoiceArgs },
  ] = useUpdateInvoiceMutation();

  const [
    getInvoiceDownloadUrl,
    { isLoading: downloadingInvoice, originalArgs: invoiceDownloadArgs },
  ] = useLazyDownloadInvoiceQuery();

  const [getPackingSlipDownloadUrl, { isLoading: downloadingPackingSlip }] =
    useLazyDownloadPackingSlipQuery();

  const { update } = useUpdateQuery();

  const [getInvoice] = useLazyGetEntityInvoiceQuery();
  const [createInvoice] = useCreateEntityInvoiceMutation();
  const [generateLinkToken] = useGenrateShortLinkMutation();

  const [getPreviewUrl] = useLazyGetPreviewUrlQuery();

  const { alertToast, successToast } = useToast();
  const dispatch = useAppDispatch();
  const { trackEvent } = useAnalytics();

  const invoiceId = invoice.uuid;
  const customerId = invoice.customer_id;
  const status = invoice.status;

  const markInvoiceAs = async (payload: {
    status: Invoice["status"];
    marked_uncollectible_at?: string | null;
    marked_void_at?: string | null;
  }) => {
    if (group?.uuid && customerId) {
      try {
        const invoice = await updateInvoice({
          entityId: entityId!,
          groupId: group?.uuid!,
          invoiceId,
          payload,
        }).unwrap();

        if (invoice.customer) {
          dispatch(
            invoiceApis.util.invalidateTags([
              { type: "INVOICE_CUSTOMERS", id: invoice.customer.uuid },
            ])
          );
        }
      } catch (error) {
        alertToast({ message: (error as BackendError).data?.error?.message });
      }
    }
  };

  const onDelete = async () => {
    try {
      await deleteInvoice({
        groupId: group.uuid,
        entityId: entityId!,
        invoiceId,
      }).unwrap();
      dispatch(
        invoiceApis.util.invalidateTags([
          { type: "INVOICE_CUSTOMERS", id: customerId },
        ])
      );
      if (isFromInvoiceDetails) {
        history.push(`/books/invoices-and-customers${search}`);
      }
      return true;
    } catch (error: any) {
      alertToast({ message: error?.data?.error?.message });
      return false;
    }
  };

  const downloadInvoice = async () => {
    if (isManualInvoice) {
      try {
        if (group.uuid && entityId) {
          const { doc_id } = await getInvoiceDownloadUrl({
            groupId: group.uuid,
            entityId,
            invoiceId: invoiceId,
          }).unwrap();

          const { download_url } = await getPreviewUrl({
            groupId: group.uuid!,
            fileId: doc_id,
          }).unwrap();
          openLink(download_url, "_blank");
        }
      } catch (error) {
        alertToast({ message: (error as BackendError)?.data?.error?.message });
      }
    }
    if (isStripeInvoice) {
      try {
        if (group.uuid && entityId) {
          const { download_url } = await getPreviewUrl({
            groupId: group.uuid!,
            fileId: invoice?.document?.file_id!,
          }).unwrap();
          openLink(download_url, "_blank");
        }
      } catch (error) {
        alertToast({ message: (error as BackendError)?.data?.error?.message });
      }
    }
  };

  const downloadPackingSlip = async () => {
    try {
      if (group.uuid && entityId) {
        const { doc_id } = await getPackingSlipDownloadUrl({
          groupId: group.uuid,
          entityId,
          invoiceId,
        }).unwrap();

        const { download_url } = await getPreviewUrl({
          groupId: group.uuid!,
          fileId: doc_id,
        }).unwrap();
        openLink(download_url, "_blank");
      }
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  const duplicate = async () => {
    setDuplicating(true);
    try {
      if (group.uuid && entityId) {
        const {
          uuid,
          customer_id,
          due_date,
          invoice_date,
          updated_at,
          document,
          customer,
          status,
          sent_to,
          sent_on,
          title,
          invoice_items,
          invoice_number,
          invoice_number_without_prefix,
          ...currentInvoice
        } = await getInvoice({
          groupId: group.uuid,
          entityId,
          invoiceId,
        }).unwrap();

        const newInvoice = await createInvoice({
          groupId: group.uuid,
          entityId,
          payload: {
            ...currentInvoice,
            customer_id: customerIdFromUrl || invoice.customer_id,
            invoice_items: invoice_items.map(({ tax_rate, ...item }) => ({
              ...item,
              uuid: undefined,
              tax_rate_id: item.tax_rate_id || tax_rate?.uuid || null,
            })),
          },
        }).unwrap();
        if (isFromInvoiceDetails) {
          openLink(
            `/books/invoices-and-customers/${newInvoice.uuid}/${search}`
          );
        } else {
          history.push(
            `/books/invoices-and-customers/${newInvoice.uuid}/${search}`
          );
        }
      }
    } catch (error: any) {
      alertToast({ message: error?.data?.error?.message });
    }
    setDuplicating(false);
  };

  const handleMarkAsSent = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    e.stopPropagation();
    trackEvent(MARKED_INVOICE_AS_SENT_FROM_THREE_DOTS);
    markInvoiceAs({
      status: INVOICE_STATUSES.DUE || INVOICE_STATUSES.OVERDUE,
    });
  };

  const markInvoiceVoidModal = useModal();
  const markAsVoid = (e: React.MouseEvent) => {
    e.stopPropagation();
    markInvoiceVoidModal.open();
  };

  const markInvoiceUncollectibleModal = useModal();
  const markAsUncollectible = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    e.stopPropagation();
    markInvoiceUncollectibleModal.open();
  };

  const deleteInvoiceModal = useModal();
  const handleDeleteClick = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    e.stopPropagation();
    const needsConfirmation =
      status === INVOICE_STATUSES.DUE || status === INVOICE_STATUSES.OVERDUE;
    if (needsConfirmation) {
      deleteInvoiceModal.open();
    } else {
      onDelete();
    }
  };

  const confirmDelete = async () => {
    const success = await onDelete();
    if (success) {
      deleteInvoiceModal.close();
      successToast({ message: "Invoice deleted successfully" });
    }
  };

  const handleVoidFromDeleteModal = () => {
    deleteInvoiceModal.close();
    markInvoiceVoidModal.open();
  };

  const confirmMarkAsVoid = async (date: string) => {
    trackEvent(MARKED_INVOICE_AS_PAID_FROM_THREE_DOTS);
    await markInvoiceAs({
      status: INVOICE_STATUSES.VOID,
      marked_void_at: date,
    });
    markInvoiceVoidModal.close();
    deleteInvoiceModal.close();
  };

  const confirmMarkAsUnCollectible = async (date: string) => {
    await markInvoiceAs({
      status: INVOICE_STATUSES.UNCOLLECTIBLE,
      marked_uncollectible_at: date,
    });
    markInvoiceUncollectibleModal.close();
  };

  const handleMarkAsPaid = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    linkViaModal.open();
    e.stopPropagation();
  };

  const writeOffPendingModal = useModal();
  const handleWriteOffPending = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    e.stopPropagation();
    writeOffPendingModal.open();
  };

  const resyncInvoiceModal = useModal();
  const handleResync = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.stopPropagation();
    resyncInvoiceModal.open();
  };

  const handleInvoiceDuplicate = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    e.stopPropagation();
    trackEvent(DUPLICATED_INVOICE_FROM_THREE_DOTS);
    duplicate();
  };

  const openSentInvoiceEditFlow = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    e.stopPropagation();
    let currentLink = `/books/invoices-and-customers/${invoiceId}${search}`;
    if (search) {
      currentLink += "&editflow=true";
    } else {
      currentLink += "?editflow=true";
    }
    history.push(currentLink);
  };

  const openSendInvoice = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.stopPropagation();
    sendInvoice.open();
  };

  const closeSendInvoiceModal = () => {
    sendInvoice.close();
    update({
      query: "is_send_invoice_open",
      value: null,
    });
  };

  const copyInvoiceLink = async () => {
    try {
      const { token } = await generateLinkToken({
        entityId,
        invoiceId: invoice.uuid,
        from: "invoice_edit_screen",
      }).unwrap();

      const origin = window.location.origin;
      await navigator.clipboard.writeText(`${origin}/invoice/${token}`);
      successToast({ message: "Invoice link has been copied" });
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  const isLoading =
    updatingInvoice ||
    downloadingInvoice ||
    downloadingPackingSlip ||
    deletingInvoice ||
    duplicating;

  const isRecordPaymentAllowed =
    status === INVOICE_STATUSES.DUE ||
    status === INVOICE_STATUSES.UNCOLLECTIBLE ||
    status === INVOICE_STATUSES.OVERDUE ||
    status === INVOICE_STATUSES.PARTIALLY_PAID;

  const isMarkAsActiveAllowed = status === INVOICE_STATUSES.DRAFT;

  const isWriteOffPendingAllowed =
    status === INVOICE_STATUSES.PARTIALLY_PAID &&
    invoice.source !== REVENUE_DATA_SOURCE.STRIPE;

  const isMarkAsVoidAllowed =
    status === INVOICE_STATUSES.DUE ||
    status === INVOICE_STATUSES.UNCOLLECTIBLE ||
    status === INVOICE_STATUSES.OVERDUE;

  const isMarkAsUncollectibleAllowed =
    status === INVOICE_STATUSES.DUE || status === INVOICE_STATUSES.OVERDUE;

  const isEditAllowed =
    status === INVOICE_STATUSES.UNCOLLECTIBLE ||
    status === INVOICE_STATUSES.DUE ||
    status === INVOICE_STATUSES.OVERDUE;

  const isCopyInvoiceLinkAllowed =
    status === INVOICE_STATUSES.UNCOLLECTIBLE ||
    status === INVOICE_STATUSES.DUE ||
    status === INVOICE_STATUSES.OVERDUE ||
    status === INVOICE_STATUSES.PAID ||
    status === INVOICE_STATUSES.VOID;

  const isRemindAllowed =
    status === INVOICE_STATUSES.DUE || status === INVOICE_STATUSES.OVERDUE;

  const isDeleteAllowed =
    status === INVOICE_STATUSES.DUE ||
    status === INVOICE_STATUSES.OVERDUE ||
    status === INVOICE_STATUSES.DRAFT;

  const currentDate = dayjs();

  const { data: bookkeepingSettings } = useGetBookkeepingSettingsQuery(
    { entityId },
    { skip: !entityId }
  );

  const lockDate = bookkeepingSettings?.accounting_lock_date
    ? dayjs(bookkeepingSettings.accounting_lock_date)
    : null;

  const voidDateMinDate = lockDate
    ? lockDate.add(1, "day").toDate()
    : undefined;

  const isInvoiceDateInLockPeriod =
    lockDate && invoice.invoice_date
      ? !dayjs(invoice.invoice_date).isAfter(lockDate)
      : false;

  // Payments and credit notes settle the invoice's AR, so the backend blocks resyncing those
  const isResyncAllowed =
    RESYNCABLE_INVOICE_SOURCES.includes(invoice.source) &&
    status !== INVOICE_STATUSES.DRAFT &&
    status !== INVOICE_STATUSES.PAID &&
    status !== INVOICE_STATUSES.PARTIALLY_PAID;

  const resyncItem = isResyncAllowed && (
    <ConditionalToolTip
      side="left"
      condition={isInvoiceDateInLockPeriod && <LockDateToolTipContent />}
    >
      <span>
        <Dropdown.Item
          disabled={isInvoiceDateInLockPeriod}
          onClick={handleResync}
        >
          <div className="t-flex t-gap-1 t-items-center">
            <span>Resync</span>
            <ToolTip
              side="bottom"
              text="Fetch the latest version of this invoice and rebuild its journal entries."
            >
              <span className="t-text-text-30 t-flex">
                <Info size="16" stroke="1.4" />
              </span>
            </ToolTip>
          </div>
        </Dropdown.Item>
      </span>
    </ConditionalToolTip>
  );

  const writeOffPendingItem = isWriteOffPendingAllowed && (
    <ConditionalToolTip
      side="left"
      condition={!customerId && "No customer assigned"}
    >
      <span>
        <Dropdown.Item disabled={!customerId} onClick={handleWriteOffPending}>
          <div className="t-flex t-gap-1 t-items-center">
            <span>Mark pending amount uncollectible</span>
            <ToolTip
              side="bottom"
              text="Customer has paid part of this invoice and won't pay the rest."
            >
              <span className="t-text-text-30 t-flex">
                <Info size="16" stroke="1.4" />
              </span>
            </ToolTip>
          </div>
        </Dropdown.Item>
      </span>
    </ConditionalToolTip>
  );

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Dropdown.Root>
        <Dropdown.Trigger asChild>
          <span>
            <Button
              customType={isFromInvoiceDetails ? "icon" : "ghost_icon"}
              size="small"
              isLoading={isLoading}
              disabled={isLoading}
            >
              <OptionDropdown color="currentColor" />
            </Button>
          </span>
        </Dropdown.Trigger>
        {isManualInvoice ? (
          <Dropdown.Content align="end" sideOffset={3}>
            <LockedUi feature={WRITE_PERMISSION_KEYS.REVENUE}>
              {isRecordPaymentAllowed && (
                <ConditionalToolTip
                  side="left"
                  condition={!customerId && "No customer assigned"}
                >
                  <span>
                    <Dropdown.Item
                      disabled={!customerId}
                      onClick={handleMarkAsPaid}
                    >
                      Record payment
                    </Dropdown.Item>
                  </span>
                </ConditionalToolTip>
              )}
              {writeOffPendingItem}
              {isMarkAsActiveAllowed && (
                <ConditionalToolTip
                  side="left"
                  condition={!customerId && "No customer assigned"}
                >
                  <span>
                    <Dropdown.Item
                      disabled={!customerId}
                      onClick={handleMarkAsSent}
                    >
                      Mark as Active
                    </Dropdown.Item>
                  </span>
                </ConditionalToolTip>
              )}
              {isMarkAsVoidAllowed && (
                <ConditionalToolTip
                  side="left"
                  condition={!customerId && "No customer assigned"}
                >
                  <span>
                    <Dropdown.Item disabled={!customerId} onClick={markAsVoid}>
                      <div className="t-flex t-gap-1 t-items-center">
                        <span>Mark as void</span>
                        <ToolTip
                          side="bottom"
                          text={
                            <>
                              Invoice issued by mistake. <br />
                              No delivery or payment expected.
                            </>
                          }
                        >
                          <span className="t-text-text-30 t-flex">
                            <Info size="16" stroke="1.4" />
                          </span>
                        </ToolTip>
                      </div>
                    </Dropdown.Item>
                  </span>
                </ConditionalToolTip>
              )}
              {isMarkAsUncollectibleAllowed && (
                <ConditionalToolTip
                  side="left"
                  condition={!customerId && "No customer assigned"}
                >
                  <span>
                    <Dropdown.Item
                      disabled={!customerId}
                      onClick={markAsUncollectible}
                    >
                      <div className="t-flex t-gap-1 t-items-center">
                        <span>Mark as uncollectible</span>
                        <ToolTip
                          side="bottom"
                          text={
                            <>
                              Service/product delivered, but payment is not
                              collectible.
                            </>
                          }
                        >
                          <span className="t-text-text-30 t-flex">
                            <Info size="16" stroke="1.4" />
                          </span>
                        </ToolTip>
                      </div>
                    </Dropdown.Item>
                  </span>
                </ConditionalToolTip>
              )}
            </LockedUi>
            <Dropdown.Item
              onClick={(e) => {
                e.stopPropagation();
                downloadInvoice();
              }}
            >
              Download
            </Dropdown.Item>
            <Dropdown.Item
              onClick={(e) => {
                e.stopPropagation();
                downloadPackingSlip();
              }}
            >
              Download packing slip
            </Dropdown.Item>

            <LockedUi feature={WRITE_PERMISSION_KEYS.REVENUE}>
              <Dropdown.Item onClick={handleInvoiceDuplicate}>
                Duplicate
              </Dropdown.Item>
              {isEditAllowed && (
                <Dropdown.Item onClick={openSentInvoiceEditFlow}>
                  Edit
                </Dropdown.Item>
              )}
            </LockedUi>
            {isCopyInvoiceLinkAllowed && (
              <Dropdown.Item onClick={copyInvoiceLink}>
                Copy Invoice link
              </Dropdown.Item>
            )}
            <LockedUi feature={WRITE_PERMISSION_KEYS.REVENUE}>
              {!isFromInvoiceDetails && isRemindAllowed && (
                <Dropdown.Item onClick={openSendInvoice}>Remind</Dropdown.Item>
              )}
              {isDeleteAllowed && (
                <Dropdown.Item onClick={handleDeleteClick}>
                  <span className="t-text-red-50">Delete</span>
                </Dropdown.Item>
              )}
            </LockedUi>
          </Dropdown.Content>
        ) : (
          <Dropdown.Content align="end">
            <LockedUi feature={WRITE_PERMISSION_KEYS.REVENUE}>
              {isRecordPaymentAllowed &&
                invoice.source !== REVENUE_DATA_SOURCE.STRIPE && (
                  <ConditionalToolTip
                    side="left"
                    condition={!customerId && "No customer assigned"}
                  >
                    <span>
                      <Dropdown.Item
                        disabled={!customerId}
                        onClick={handleMarkAsPaid}
                      >
                        Record payment
                      </Dropdown.Item>
                    </span>
                  </ConditionalToolTip>
                )}
              {writeOffPendingItem}
              {resyncItem}
              {isEditAllowed && (isBillComInvoice || isHubspotInvoice) && (
                <Dropdown.Item onClick={openSentInvoiceEditFlow}>
                  Edit
                </Dropdown.Item>
              )}
            </LockedUi>
            <Dropdown.Item
              disabled={!invoice.document?.file_id}
              onClick={(e) => {
                e.stopPropagation();
                downloadInvoice();
              }}
            >
              Download
            </Dropdown.Item>
          </Dropdown.Content>
        )}

        {sendInvoice.isOpen && (
          <div onClick={(e) => e.stopPropagation()}>
            <SendInvoiceModal
              isOpen={sendInvoice.isOpen}
              close={closeSendInvoiceModal}
              invoiceIdFromProp={invoiceId}
              isReminder
            />
          </div>
        )}
      </Dropdown.Root>

      <div onClick={(e) => e.stopPropagation()}>
        <LinkInvoiceToTransactionModal
          invoiceId={invoiceId}
          isOpen={linkViaModal.isOpen}
          close={linkViaModal.close}
        />

        {resyncInvoiceModal.isOpen && (
          <ResyncInvoiceModal
            invoice={invoice}
            open={resyncInvoiceModal.isOpen}
            onClose={resyncInvoiceModal.close}
          />
        )}

        {writeOffPendingModal.isOpen && (
          <WriteOffPendingAmountModal
            invoice={invoice}
            open={writeOffPendingModal.isOpen}
            onClose={writeOffPendingModal.close}
          />
        )}

        <Modal.Root
          open={markInvoiceUncollectibleModal.isOpen}
          onOpenChange={markInvoiceUncollectibleModal.close}
        >
          <Modal.Content>
            <FormikForm
              initialValues={{
                date: currentDate.format("YYYY-MM-DD"),
                invoiceDate: false,
              }}
              onSubmit={() => {}}
            >
              {({ setFieldValue, values }) => (
                <>
                  <Modal.Header>
                    <Modal.Title>Mark invoice as uncollectible</Modal.Title>
                    <Modal.Close />
                  </Modal.Header>
                  <Modal.Body>
                    You're about to mark the invoice{" "}
                    <b>{invoice.invoice_number}</b> as uncollectible.
                    <br />
                    You can later update the status to void/paid accordingly.
                    <br /> Are you sure?
                    <div className="t-flex t-flex-col t-gap-5 t-mt-5">
                      <ConditionalToolTip
                        condition={values.invoiceDate}
                        side="bottom"
                        text="Date set to the invoice date, unselect the checkbox to change"
                      >
                        <div className="t-w-full">
                          <DateField
                            name="date"
                            dateProps={{
                              label: (
                                <span>
                                  Effective date to mark uncollectible
                                  <ToolTip
                                    text="Select the effective date for marking this transaction as uncollectible. An adjusting journal entry will be created for this date."
                                    side="bottom"
                                  >
                                    <span className="t-text-text-30 t-ml-1">
                                      <SmallInfo />
                                    </span>
                                  </ToolTip>
                                </span>
                              ),
                              required: true,
                              rightComponent: (
                                <span className="t-flex t-items-center">
                                  <CalendarIcon className="t-text-text-30 t-ml-2" />
                                </span>
                              ),
                              readOnly: values.invoiceDate,
                              disabled: values.invoiceDate,
                            }}
                          />
                        </div>
                      </ConditionalToolTip>
                      <Checkbox
                        name="invoiceDate"
                        label={
                          <span className="t-text-text-30">
                            Set to invoice date
                          </span>
                        }
                        checked={values.invoiceDate}
                        className="t-mt-3"
                        onChange={(e) => {
                          setFieldValue("invoiceDate", e.target.checked);
                          if (e.target.checked && invoice.invoice_date) {
                            setFieldValue("date", invoice.invoice_date);
                          }
                        }}
                      />
                    </div>
                  </Modal.Body>
                  <Modal.FooterButtonGroup>
                    <Modal.RawClose asChild>
                      <Button>Close</Button>
                    </Modal.RawClose>
                    <Button
                      customType="primary"
                      onClick={() => confirmMarkAsUnCollectible(values.date)}
                      isLoading={updatingInvoice}
                    >
                      Confirm
                    </Button>
                  </Modal.FooterButtonGroup>
                </>
              )}
            </FormikForm>
          </Modal.Content>
        </Modal.Root>

        <Modal.Root
          open={markInvoiceVoidModal.isOpen}
          onOpenChange={markInvoiceVoidModal.close}
        >
          <Modal.Content>
            <FormikForm
              initialValues={{
                date: currentDate.format("YYYY-MM-DD"),
                invoiceDate: false,
              }}
              onSubmit={() => {}}
            >
              {({ setFieldValue, values }) => (
                <>
                  <Modal.Header>
                    <Modal.Title>Mark invoice as void</Modal.Title>
                    <Modal.Close />
                  </Modal.Header>
                  <Modal.Body>
                    You're about to mark the invoice{" "}
                    <b>{invoice.invoice_number}</b> as void.
                    <br />
                    This is a final action and cannot be reversed.
                    <br />
                    Are you sure?
                    <div className="t-flex t-flex-col t-gap-5 t-mt-5">
                      <ConditionalToolTip
                        condition={values.invoiceDate}
                        side="bottom"
                        text="Date set to the invoice date, unselect the checkbox to change"
                      >
                        <div>
                          <DateField
                            name="date"
                            dateProps={{
                              label: (
                                <span className="t-inline-flex t-items-center">
                                  Effective date to void
                                  <ToolTip
                                    text="Select the effective date for marking this transaction as void. An adjusting journal entry will be created for this date."
                                    side="top"
                                  >
                                    <span className="t-text-text-30 t-ml-1 t-flex t-items-center">
                                      <SmallInfo />
                                    </span>
                                  </ToolTip>
                                </span>
                              ),
                              required: true,
                              rightComponent: (
                                <CalendarIcon className="t-text-text-30 " />
                              ),
                              readOnly: values.invoiceDate,
                              disabled: values.invoiceDate,
                              minDate: voidDateMinDate,
                              maxDate: currentDate.toDate(),
                              getDisabledTooltipContent: (date) => {
                                if (
                                  lockDate &&
                                  !dayjs(date).isAfter(lockDate)
                                ) {
                                  return <LockDateToolTipContent />;
                                }
                                return "";
                              },
                            }}
                          />
                        </div>
                      </ConditionalToolTip>
                      {!isInvoiceDateInLockPeriod && (
                        <Checkbox
                          name="invoiceDate"
                          label={
                            <span className="t-text-text-30">
                              Set to invoice date
                            </span>
                          }
                          className="t-mt-3"
                          onChange={(e) => {
                            setFieldValue("invoiceDate", e.target.checked);
                            if (e.target.checked && invoice.invoice_date) {
                              setFieldValue("date", invoice.invoice_date);
                            }
                          }}
                        />
                      )}
                    </div>
                  </Modal.Body>
                  <Modal.FooterButtonGroup>
                    <Modal.RawClose asChild>
                      <Button>Close</Button>
                    </Modal.RawClose>
                    <Button
                      customType="primary"
                      onClick={() => confirmMarkAsVoid(values.date)}
                      isLoading={updatingInvoice}
                    >
                      Confirm
                    </Button>
                  </Modal.FooterButtonGroup>
                </>
              )}
            </FormikForm>
          </Modal.Content>
        </Modal.Root>

        <Modal.Root
          open={deleteInvoiceModal.isOpen}
          onOpenChange={deleteInvoiceModal.close}
        >
          <DeleteInvoiceWarningModal
            onVoid={handleVoidFromDeleteModal}
            onDelete={confirmDelete}
            isDeleteLoading={deletingInvoice}
          />
        </Modal.Root>
      </div>
    </div>
  );
};
